import {
    Box,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Chip,
    IconButton,
    Alert,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Paper,
} from "@mui/material";
import {
    Edit as EditIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Add as AddIcon,
    Save as SaveIcon,
    Undo as UndoIcon,
    UploadFile as UploadFileIcon,
    Code as CodeIcon,
    ViewList as ViewListIcon,
    GridView as GridViewIcon,
} from "@mui/icons-material";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import Editor, { OnMount, Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { RootState } from "../../configureStore";
import { useConfigEditorActions } from "../../hooks/useConfigEditorActions";
import { ConfigDetail } from "../../interface/configs";
import { FormConfig, FieldConfig, PageConfig, SectionConfig, GridConfig, ArrayFieldConfig } from "./TaxReturnWebforms/types/formConfig";
import { developerList, promptManagementList } from "../../utils";
import { useTheme } from "../../ThemeContext";
import FieldEditDialog from "./FieldEditDialog";
import SectionEditDialog from "./SectionEditDialog";
import GridEditDialog from "./GridEditDialog";
import ConfigEditorField from "./ConfigEditorField";
import Button from "../atoms/Button";

// Recursive component for rendering nested itemFields
interface ItemFieldsListProps {
    itemFields: FieldConfig[];
    pageIdx: number;
    sectionIdx: number;
    gridIdx: number;
    fieldIdx: number;
    onToggleImportant: (pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number, itemFieldIdx?: number) => void;
    onEditItemField: (itemField: FieldConfig, pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number, itemFieldIdx: number) => void;
    onAddItemField?: (pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number) => void;
    depth: number;
}

const ItemFieldsList = ({
    itemFields,
    pageIdx,
    sectionIdx,
    gridIdx,
    fieldIdx,
    onToggleImportant,
    onEditItemField,
    onAddItemField,
    depth,
}: ItemFieldsListProps) => {
    const borderColors = ['primary.light', 'secondary.light', 'success.light', 'info.light'];
    const borderColor = borderColors[depth % borderColors.length];

    return (
        <Box sx={{
            ml: 4,
            mt: 1,
            pl: 2,
            borderLeft: 2,
            borderColor,
        }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Item Fields ({itemFields.length})
            </Typography>
            {itemFields.map((itemField: FieldConfig, itemFieldIdx: number) => (
                <Box key={itemFieldIdx}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            bgcolor: itemField.important ? 'warning.lighter' : 'action.hover',
                            border: itemField.important ? '1px solid' : 'none',
                            borderColor: 'warning.main',
                            '&:hover': {
                                bgcolor: itemField.important ? 'warning.light' : 'action.selected',
                            },
                        }}
                    >
                        <Tooltip title={itemField.important ? "Remove important" : "Mark as important"}>
                            <IconButton
                                size="small"
                                onClick={() => onToggleImportant(pageIdx, sectionIdx, gridIdx, fieldIdx, itemFieldIdx)}
                                sx={{ p: 0.5 }}
                            >
                                {itemField.important ? (
                                    <StarIcon fontSize="small" color="warning" />
                                ) : (
                                    <StarBorderIcon fontSize="small" color="action" />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                                {itemField.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {itemField.fieldPath}
                            </Typography>
                        </Box>
                        {itemField.type && (
                            <Chip label={itemField.type} size="small" variant="outlined" />
                        )}
                        {itemField.colSpan && (
                            <Chip label={`span:${itemField.colSpan}`} size="small" variant="outlined" />
                        )}
                        <IconButton
                            size="small"
                            onClick={() => onEditItemField(itemField, pageIdx, sectionIdx, gridIdx, fieldIdx, itemFieldIdx)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {/* Recursively render nested array itemFields */}
                    {itemField.type === 'array' && (itemField as ArrayFieldConfig).itemFields && (
                        <ItemFieldsList
                            itemFields={(itemField as ArrayFieldConfig).itemFields}
                            pageIdx={pageIdx}
                            sectionIdx={sectionIdx}
                            gridIdx={gridIdx}
                            fieldIdx={fieldIdx}
                            onToggleImportant={onToggleImportant}
                            onEditItemField={onEditItemField}
                            onAddItemField={onAddItemField}
                            depth={depth + 1}
                        />
                    )}
                </Box>
            ))}
            {onAddItemField && depth === 0 && (
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onAddItemField(pageIdx, sectionIdx, gridIdx, fieldIdx)}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                >
                    Add Item Field
                </Button>
            )}
        </Box>
    );
};

// Recursive component for rendering nested subFields (fields that have subFields property).
// Each level is wrapped in a Paper and lays fields out in a CSS grid using the parent field's
// subFieldColumns count, mirroring the runtime form renderer.
interface SubFieldsListProps {
    parentField: FieldConfig;
    subFields: FieldConfig[];
    pageIdx: number;
    sectionIdx: number;
    gridIdx: number;
    fieldIdx: number;
    parentSubFieldPath: number[]; // path to the parent field whose subFields are being rendered
    onToggleImportant: (pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number, itemFieldIdx?: number, subFieldPath?: number[]) => void;
    onEdit: (subField: FieldConfig, pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number, subFieldPath: number[]) => void;
    onAdd?: (pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number, parentSubFieldPath: number[]) => void;
    depth: number;
}

const SubFieldsList = ({
    parentField,
    subFields,
    pageIdx,
    sectionIdx,
    gridIdx,
    fieldIdx,
    parentSubFieldPath,
    onToggleImportant,
    onEdit,
    onAdd,
    depth,
}: SubFieldsListProps) => {
    const columns = parentField.subFieldColumns ?? 1;

    return (
        <Paper
            variant="outlined"
            sx={{
                ml: 3,
                mt: 1,
                p: 1.5,
                bgcolor: 'action.hover',
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Sub Fields ({subFields.length})
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gap: 2,
                }}
            >
                {subFields.map((subField: FieldConfig, subIdx: number) => {
                    const thisPath = [...parentSubFieldPath, subIdx];
                    const fieldId = `subfield-${pageIdx}-${sectionIdx}-${gridIdx}-${fieldIdx}-${thisPath.join('.')}`;
                    return (
                        <Box
                            key={fieldId}
                            sx={{
                                gridColumn: subField.colSpan ? `span ${subField.colSpan}` : undefined,
                                minWidth: 0,
                            }}
                        >
                            <ConfigEditorField
                                field={subField}
                                fieldId={fieldId}
                                onEdit={() => onEdit(subField, pageIdx, sectionIdx, gridIdx, fieldIdx, thisPath)}
                                onToggleImportant={() => onToggleImportant(pageIdx, sectionIdx, gridIdx, fieldIdx, undefined, thisPath)}
                                disableDrag
                            />
                            {subField.subFields && subField.subFields.length > 0 && (
                                <SubFieldsList
                                    parentField={subField}
                                    subFields={subField.subFields}
                                    pageIdx={pageIdx}
                                    sectionIdx={sectionIdx}
                                    gridIdx={gridIdx}
                                    fieldIdx={fieldIdx}
                                    parentSubFieldPath={thisPath}
                                    onToggleImportant={onToggleImportant}
                                    onEdit={onEdit}
                                    onAdd={onAdd}
                                    depth={depth + 1}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
            {onAdd && (
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onAdd(pageIdx, sectionIdx, gridIdx, fieldIdx, parentSubFieldPath)}
                    sx={{ mt: 1 }}
                >
                    Add Sub Field
                </Button>
            )}
        </Paper>
    );
};

const ConfigEditor = () => {
    const { fetchList, save, refetchSelected } = useConfigEditorActions();
    const { accounts } = useMsal();
    const { mode } = useTheme();
    const selectedConfig = useSelector<RootState, ConfigDetail | null>(({ configs }) => configs.selectedConfig);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['configs/GET_CONFIG_CONTENT']);
    const updateLoading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['configs/UPDATE_CONFIG']);

    const [editedConfig, setEditedConfig] = useState<FormConfig | null>(null);
    const [selectedPage, setSelectedPage] = useState<number>(0);
    const [editingField, setEditingField] = useState<{
        field: FieldConfig;
        pageIdx: number;
        sectionIdx: number;
        gridIdx: number;
        fieldIdx: number;
        itemFieldIdx?: number; // For editing fields within an array's itemFields
        subFieldPath?: number[]; // Path of subField indices from the root field
    } | null>(null);
    const [editingSection, setEditingSection] = useState<{
        section: SectionConfig;
        pageIdx: number;
        sectionIdx: number;
    } | null>(null);
    const [editingGrid, setEditingGrid] = useState<{
        grid: GridConfig;
        pageIdx: number;
        sectionIdx: number;
        gridIdx: number;
    } | null>(null);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [editMode, setEditMode] = useState<'ui' | 'json'>('ui');
    const [jsonText, setJsonText] = useState<string>('');
    const [jsonError, setJsonError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const monacoRef = useRef<Monaco | null>(null);

    // Check if user has elevated permissions for full CRUD (add/delete fields)
    const canFullCrud = accounts[0] && developerList.includes(accounts[0].username.toLowerCase());

    // Check if user can directly edit JSON
    const canEditJson = accounts[0] && promptManagementList.includes(accounts[0].username.toLowerCase());

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadError(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const content = e.target?.result as string;
                const parsed = JSON.parse(content) as FormConfig;

                // Basic validation - check required fields
                if (!parsed.formType || !parsed.formName || !parsed.pages) {
                    setUploadError('Invalid config format: missing formType, formName, or pages');
                    return;
                }

                // Validate pages structure
                if (!Array.isArray(parsed.pages) || parsed.pages.length === 0) {
                    setUploadError('Invalid config format: pages must be a non-empty array');
                    return;
                }

                setEditedConfig(parsed);
                setHasChanges(true);
                setSaveSuccess(false);
            } catch {
                setUploadError('Failed to parse JSON file. Please ensure it is valid JSON.');
            }
        };
        reader.onerror = () => {
            setUploadError('Failed to read file');
        };
        reader.readAsText(file);

        // Reset input so same file can be uploaded again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (selectedConfig) {
            const configCopy = JSON.parse(JSON.stringify(selectedConfig.content));
            setEditedConfig(configCopy);
            setJsonText(JSON.stringify(configCopy, null, 2));
            setJsonError(null);
            setHasChanges(false);
            setSelectedPage(0);
        }
    }, [selectedConfig]);

    // Sync JSON text when switching to JSON mode
    useEffect(() => {
        if (editMode === 'json' && editedConfig) {
            setJsonText(JSON.stringify(editedConfig, null, 2));
            setJsonError(null);
        }
    }, [editMode, editedConfig]);

    // In USE_LOCAL_CONFIGS mode, pull the current JSON from disk on mount so
    // Fast Refresh picks up edits made to the local files without a reload.
    useEffect(() => {
        refetchSelected();
    }, [refetchSelected]);

    // Count changes
    const _changeCount = useMemo(() => {
        if (!selectedConfig || !editedConfig) return 0;
        const original = JSON.stringify(selectedConfig.content);
        const edited = JSON.stringify(editedConfig);
        return original === edited ? 0 : 1; // Simplified - just track if any changes
    }, [selectedConfig, editedConfig]);

    const handlePageChange = (_: React.SyntheticEvent, newValue: number) => {
        setSelectedPage(newValue);
    };

    // Navigate into a nested subFields chain. Returns the field at the given path.
    const getSubFieldAt = (rootField: FieldConfig, subFieldPath: number[]): FieldConfig => {
        let current: FieldConfig = rootField;
        subFieldPath.forEach((idx) => {
            current = (current.subFields as FieldConfig[])[idx];
        });
        return current;
    };

    // Returns the parent that directly contains the subField at subFieldPath, and the final index.
    const getSubFieldParent = (rootField: FieldConfig, subFieldPath: number[]): { parent: FieldConfig; lastIdx: number } => {
        const lastIdx = subFieldPath[subFieldPath.length - 1];
        let parent: FieldConfig = rootField;
        for (let i = 0; i < subFieldPath.length - 1; i += 1) {
            parent = (parent.subFields as FieldConfig[])[subFieldPath[i]];
        }
        return { parent, lastIdx };
    };

    const handleEditField = (
        field: FieldConfig,
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
        fieldIdx: number,
    ) => {
        setEditingField({ field, pageIdx, sectionIdx, gridIdx, fieldIdx });
    };

    const handleSaveField = (updated: FieldConfig) => {
        if (!editedConfig || !editingField) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const grid = newConfig.pages[editingField.pageIdx]
            .sections[editingField.sectionIdx]
            .grids[editingField.gridIdx];

        if (editingField.itemFieldIdx !== undefined) {
            // Editing an itemField within an array field
            const arrayField = grid.fields[editingField.fieldIdx] as ArrayFieldConfig;
            arrayField.itemFields[editingField.itemFieldIdx] = updated;
        } else if (editingField.subFieldPath && editingField.subFieldPath.length > 0) {
            // Editing a nested subField
            const rootField = grid.fields[editingField.fieldIdx];
            const { parent, lastIdx } = getSubFieldParent(rootField, editingField.subFieldPath);
            (parent.subFields as FieldConfig[])[lastIdx] = updated;
        } else {
            // Editing a regular field
            grid.fields[editingField.fieldIdx] = updated;
        }

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
        setEditingField(null);
    };

    const handleDeleteField = () => {
        if (!editedConfig || !editingField || !canFullCrud) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const grid = newConfig.pages[editingField.pageIdx]
            .sections[editingField.sectionIdx]
            .grids[editingField.gridIdx];

        if (editingField.itemFieldIdx !== undefined) {
            // Deleting an itemField within an array field
            const arrayField = grid.fields[editingField.fieldIdx] as ArrayFieldConfig;
            arrayField.itemFields.splice(editingField.itemFieldIdx, 1);
        } else if (editingField.subFieldPath && editingField.subFieldPath.length > 0) {
            // Deleting a nested subField
            const rootField = grid.fields[editingField.fieldIdx];
            const { parent, lastIdx } = getSubFieldParent(rootField, editingField.subFieldPath);
            (parent.subFields as FieldConfig[]).splice(lastIdx, 1);
        } else {
            // Deleting a regular field
            grid.fields.splice(editingField.fieldIdx, 1);
        }

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
        setEditingField(null);
    };

    const handleToggleImportant = (
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
        fieldIdx: number,
        itemFieldIdx?: number,
        subFieldPath?: number[],
    ) => {
        if (!editedConfig) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const rootField = newConfig.pages[pageIdx].sections[sectionIdx].grids[gridIdx].fields[fieldIdx];

        let field: FieldConfig;
        if (itemFieldIdx !== undefined) {
            field = (rootField as ArrayFieldConfig).itemFields[itemFieldIdx];
        } else if (subFieldPath && subFieldPath.length > 0) {
            field = getSubFieldAt(rootField, subFieldPath);
        } else {
            field = rootField;
        }

        if (field.important) {
            delete field.important;
        } else {
            field.important = true;
        }

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
    };

    const handleEditItemField = (
        itemField: FieldConfig,
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
        fieldIdx: number,
        itemFieldIdx: number,
    ) => {
        setEditingField({ field: itemField, pageIdx, sectionIdx, gridIdx, fieldIdx, itemFieldIdx });
    };

    const handleEditSubField = (
        subField: FieldConfig,
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
        fieldIdx: number,
        subFieldPath: number[],
    ) => {
        setEditingField({ field: subField, pageIdx, sectionIdx, gridIdx, fieldIdx, subFieldPath });
    };

    const handleAddSubField = (
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
        fieldIdx: number,
        parentSubFieldPath: number[], // path to the parent field whose subFields we're adding to
    ) => {
        if (!editedConfig || !canFullCrud) return;

        const newSubField: FieldConfig = {
            fieldPath: 'new.subField',
            label: 'New Sub Field',
        };

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const rootField = newConfig.pages[pageIdx].sections[sectionIdx].grids[gridIdx].fields[fieldIdx];
        const parent = parentSubFieldPath.length === 0 ? rootField : getSubFieldAt(rootField, parentSubFieldPath);
        if (!parent.subFields) parent.subFields = [];
        parent.subFields.push(newSubField);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);

        // Open edit dialog for the new subField
        const newIdx = parent.subFields.length - 1;
        setEditingField({
            field: newSubField,
            pageIdx,
            sectionIdx,
            gridIdx,
            fieldIdx,
            subFieldPath: [...parentSubFieldPath, newIdx],
        });
    };

    const handleAddItemField = (pageIdx: number, sectionIdx: number, gridIdx: number, fieldIdx: number) => {
        if (!editedConfig || !canFullCrud) return;

        const newItemField: FieldConfig = {
            fieldPath: 'newField',
            label: 'New Item Field',
        };

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const arrayField = newConfig.pages[pageIdx]
            .sections[sectionIdx]
            .grids[gridIdx]
            .fields[fieldIdx] as ArrayFieldConfig;
        arrayField.itemFields.push(newItemField);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);

        // Open edit dialog for the new item field
        const itemFieldIdx = arrayField.itemFields.length - 1;
        setEditingField({
            field: newItemField,
            pageIdx,
            sectionIdx,
            gridIdx,
            fieldIdx,
            itemFieldIdx,
        });
    };

    const handleAddField = (pageIdx: number, sectionIdx: number, gridIdx: number) => {
        if (!editedConfig || !canFullCrud) return;

        const newField: FieldConfig = {
            fieldPath: 'new.field.path',
            label: 'New Field',
        };

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[pageIdx]
            .sections[sectionIdx]
            .grids[gridIdx]
            .fields.push(newField);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);

        // Open edit dialog for the new field
        const fieldIdx = newConfig.pages[pageIdx].sections[sectionIdx].grids[gridIdx].fields.length - 1;
        setEditingField({
            field: newField,
            pageIdx,
            sectionIdx,
            gridIdx,
            fieldIdx,
        });
    };

    // Section handlers
    const handleEditSection = (section: SectionConfig, pageIdx: number, sectionIdx: number) => {
        setEditingSection({ section, pageIdx, sectionIdx });
    };

    const handleSaveSection = (updated: SectionConfig) => {
        if (!editedConfig || !editingSection) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[editingSection.pageIdx].sections[editingSection.sectionIdx] = updated;

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
        setEditingSection(null);
    };

    const handleDeleteSection = () => {
        if (!editedConfig || !editingSection || !canFullCrud) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[editingSection.pageIdx].sections.splice(editingSection.sectionIdx, 1);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
        setEditingSection(null);
    };

    const handleAddSection = (pageIdx: number) => {
        if (!editedConfig || !canFullCrud) return;

        const newSection: SectionConfig = {
            title: 'New Section',
            grids: [{
                columns: 2,
                fields: [],
            }],
        };

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[pageIdx].sections.push(newSection);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);

        // Open edit dialog for the new section
        const sectionIdx = newConfig.pages[pageIdx].sections.length - 1;
        setEditingSection({
            section: newSection,
            pageIdx,
            sectionIdx,
        });
    };

    // Grid handlers
    const handleEditGrid = (
        grid: GridConfig,
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
    ) => {
        setEditingGrid({ grid, pageIdx, sectionIdx, gridIdx });
    };

    const handleSaveGrid = (updated: GridConfig) => {
        if (!editedConfig || !editingGrid) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[editingGrid.pageIdx]
            .sections[editingGrid.sectionIdx]
            .grids[editingGrid.gridIdx] = updated;

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
        setEditingGrid(null);
    };

    const handleDeleteGrid = () => {
        if (!editedConfig || !editingGrid || !canFullCrud) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[editingGrid.pageIdx]
            .sections[editingGrid.sectionIdx]
            .grids.splice(editingGrid.gridIdx, 1);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
        setEditingGrid(null);
    };

    const handleToggleGridRowImportant = (
        pageIdx: number,
        sectionIdx: number,
        gridIdx: number,
    ) => {
        if (!editedConfig) return;

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const grid = newConfig.pages[pageIdx].sections[sectionIdx].grids[gridIdx];
        const currentImportant = Boolean(
            grid.important
            || grid.fieldImportantTemplate
            || grid.fields.some((f) => f.important),
        );
        const nextImportant = !currentImportant;

        if (nextImportant) {
            grid.important = true;
            // Mark row values as important so runtime fields inherit the flag.
            if (grid.fieldPathTemplate) {
                grid.fieldImportantTemplate = true;
            }
            grid.fields = grid.fields.map((f) => ({ ...f, important: true }));
        } else {
            delete grid.important;
            if (grid.fieldPathTemplate) {
                delete grid.fieldImportantTemplate;
            }
            grid.fields = grid.fields.map((f) => {
                const { important: _dropped, ...rest } = f;
                return rest as FieldConfig;
            });
        }

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
    };

    const handleAddGrid = (pageIdx: number, sectionIdx: number) => {
        if (!editedConfig || !canFullCrud) return;

        const newGrid: GridConfig = {
            columns: 2,
            fields: [],
        };

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        newConfig.pages[pageIdx].sections[sectionIdx].grids.push(newGrid);

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);

        // Open edit dialog for the new grid
        const gridIdx = newConfig.pages[pageIdx].sections[sectionIdx].grids.length - 1;
        setEditingGrid({
            grid: newGrid,
            pageIdx,
            sectionIdx,
            gridIdx,
        });
    };

    const handleSave = () => {
        if (!selectedConfig || !editedConfig) return;

        const configName = selectedConfig.name.replace('configs/', '').replace('.json', '');
        save({
            input: {
                configName,
                content: editedConfig,
            },
            onSuccess: () => {
                setSaveSuccess(true);
                setHasChanges(false);
                fetchList();
            },
        });
    };

    const handleRevert = () => {
        if (selectedConfig) {
            const configCopy = JSON.parse(JSON.stringify(selectedConfig.content));
            setEditedConfig(configCopy);
            setJsonText(JSON.stringify(configCopy, null, 2));
            setJsonError(null);
            setHasChanges(false);
            setSaveSuccess(false);
        }
    };

    const handleEditModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'ui' | 'json' | null) => {
        if (newMode !== null) {
            setEditMode(newMode);
        }
    };

    // Drag-and-drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Handle drag end for field reordering
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !editedConfig) return;

        // Parse the field IDs to get indices
        // Format: "field-{pageIdx}-{sectionIdx}-{gridIdx}-{fieldIdx}"
        const activeIdParts = (active.id as string).split('-');
        const overIdParts = (over.id as string).split('-');

        // Only handle reordering within the same grid
        if (
            activeIdParts[1] !== overIdParts[1]
            || activeIdParts[2] !== overIdParts[2]
            || activeIdParts[3] !== overIdParts[3]
        ) {
            return;
        }

        const pageIdx = parseInt(activeIdParts[1], 10);
        const sectionIdx = parseInt(activeIdParts[2], 10);
        const gridIdx = parseInt(activeIdParts[3], 10);
        const oldFieldIdx = parseInt(activeIdParts[4], 10);
        const newFieldIdx = parseInt(overIdParts[4], 10);

        const newConfig = JSON.parse(JSON.stringify(editedConfig)) as FormConfig;
        const { fields } = newConfig.pages[pageIdx].sections[sectionIdx].grids[gridIdx];

        newConfig.pages[pageIdx].sections[sectionIdx].grids[gridIdx].fields = arrayMove(
            fields,
            oldFieldIdx,
            newFieldIdx,
        );

        setEditedConfig(newConfig);
        setHasChanges(true);
        setSaveSuccess(false);
    };

    const handleJsonChange = useCallback((newText: string | undefined) => {
        const text = newText ?? '';
        setJsonText(text);
        setSaveSuccess(false);

        try {
            const parsed = JSON.parse(text) as FormConfig;

            // Basic validation
            if (!parsed.formType || !parsed.formName || !parsed.pages) {
                setJsonError('Invalid config format: missing formType, formName, or pages');
                return;
            }

            if (!Array.isArray(parsed.pages) || parsed.pages.length === 0) {
                setJsonError('Invalid config format: pages must be a non-empty array');
                return;
            }

            setJsonError(null);
            setEditedConfig(parsed);
            setHasChanges(true);
        } catch {
            setJsonError('Invalid JSON syntax');
        }
    }, []);

    // Monaco editor mount handler
    const handleEditorDidMount: OnMount = (monacoEditor, monacoInstance) => {
        editorRef.current = monacoEditor;
        monacoRef.current = monacoInstance;
        monacoEditor.focus();
    };

    // Get Monaco theme based on app theme
    const getMonacoTheme = () => (mode === 'dark' ? 'vs-dark' : 'light');

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!selectedConfig || !editedConfig) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">
                    Select a config to edit
                </Typography>
            </Box>
        );
    }

    const currentPage: PageConfig | undefined = editedConfig.pages[selectedPage];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Field Edit Dialog */}
            <FieldEditDialog
                field={editingField?.field || null}
                open={!!editingField}
                onClose={() => setEditingField(null)}
                onSave={handleSaveField}
                onDelete={canFullCrud ? handleDeleteField : undefined}
                canDelete={canFullCrud}
            />

            {/* Section Edit Dialog */}
            <SectionEditDialog
                section={editingSection?.section || null}
                open={!!editingSection}
                onClose={() => setEditingSection(null)}
                onSave={handleSaveSection}
                onDelete={canFullCrud ? handleDeleteSection : undefined}
                canDelete={canFullCrud}
            />

            {/* Grid Edit Dialog */}
            <GridEditDialog
                grid={editingGrid?.grid || null}
                open={!!editingGrid}
                onClose={() => setEditingGrid(null)}
                onSave={handleSaveGrid}
                onDelete={canFullCrud ? handleDeleteGrid : undefined}
                canDelete={canFullCrud}
                isColumnarSection={
                    editingGrid
                        ? editedConfig?.pages[editingGrid.pageIdx]
                            ?.sections[editingGrid.sectionIdx]
                            ?.layout === 'columnar-properties'
                        : false
                }
            />

            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">
                            {editedConfig.formName}
                        </Typography>
                        <Chip label={editedConfig.formType} size="small" color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {canEditJson && (
                            <ToggleButtonGroup
                                value={editMode}
                                exclusive
                                onChange={handleEditModeChange}
                                size="small"
                            >
                                <ToggleButton value="ui">
                                    <Tooltip title="Visual Editor">
                                        <ViewListIcon fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="json">
                                    <Tooltip title="JSON Editor">
                                        <CodeIcon fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {hasChanges && (
                            <Chip label="Unsaved changes" color="warning" size="small" />
                        )}
                    </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Last modified: {new Date(selectedConfig.lastModified).toLocaleString()}
                    {selectedConfig.versionId && ` | Version: ${selectedConfig.versionId.substring(0, 20)}...`}
                </Typography>
            </Box>

            {/* JSON Editor Mode */}
            {editMode === 'json' && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                    {jsonError && (
                        <Alert severity="error" sx={{ m: 2, mb: 0 }}>
                            {jsonError}
                        </Alert>
                    )}
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <Editor
                            height="100%"
                            language="json"
                            theme={getMonacoTheme()}
                            value={jsonText}
                            onChange={handleJsonChange}
                            onMount={handleEditorDidMount}
                            options={{
                                fontSize: 14,
                                fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                                minimap: { enabled: false },
                                lineNumbers: 'on',
                                wordWrap: 'on',
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                tabSize: 2,
                                renderWhitespace: 'selection',
                                bracketPairColorization: { enabled: true },
                                cursorBlinking: 'smooth',
                                cursorSmoothCaretAnimation: 'on',
                                smoothScrolling: true,
                                padding: { top: 8, bottom: 8 },
                                folding: true,
                                foldingHighlight: true,
                                showFoldingControls: 'mouseover',
                                formatOnPaste: true,
                                formatOnType: true,
                            }}
                            loading={(
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        />
                    </Box>
                </Box>
            )}

            {/* UI Editor Mode */}
            {editMode === 'ui' && (
                <>
                    {/* Page Tabs */}
                    {editedConfig.pages.length > 1 && (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={selectedPage} onChange={handlePageChange}>
                                {editedConfig.pages.map((page, idx) => (
                                    <Tab key={idx} label={`Page ${page.pageNumber}`} />
                                ))}
                            </Tabs>
                        </Box>
                    )}

                    {/* Sections */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {currentPage?.sections.map((section: SectionConfig, sectionIdx: number) => (
                            <Box key={sectionIdx} sx={{ mb: 3 }}>
                                {section.showDivider && <Divider sx={{ my: 2 }} />}
                                {/* Section Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {section.title}
                                    </Typography>
                                    {section.layout && (
                                        <Chip
                                            label={section.layout}
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                        />
                                    )}
                                    {(canFullCrud || canEditJson) && (
                                        <Tooltip title="Edit section">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditSection(section, selectedPage, sectionIdx)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>

                                {/* Columnar layout header */}
                                {section.layout === 'columnar-properties' && section.columnLabels && section.columnLabels.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: `200px repeat(${section.columnLabels.length}, 1fr)`,
                                            gap: 1,
                                            mb: 1,
                                            pb: 1,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box />
                                        {section.columnLabels.map((label, labelIdx) => (
                                            <Typography
                                                key={labelIdx}
                                                variant="caption"
                                                fontWeight={600}
                                                sx={{ textAlign: 'center' }}
                                            >
                                                {typeof label === 'string' ? label : label.label}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {/* Grids */}
                                {section.grids.map((grid: GridConfig, gridIdx: number) => {
                                    const gridColumns = typeof grid.columns === 'number'
                                        ? `repeat(${grid.columns}, 1fr)`
                                        : grid.columns
                                            ? {
                                                xs: grid.columns.xs ? `repeat(${grid.columns.xs}, 1fr)` : '1fr',
                                                sm: grid.columns.sm ? `repeat(${grid.columns.sm}, 1fr)` : undefined,
                                                md: grid.columns.md ? `repeat(${grid.columns.md}, 1fr)` : undefined,
                                                lg: grid.columns.lg ? `repeat(${grid.columns.lg}, 1fr)` : undefined,
                                            }
                                            : 'repeat(2, 1fr)';

                                    return (
                                        <Box key={gridIdx} sx={{ mb: 2 }}>
                                            {/* Grid Header */}
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                            }}
                                            >
                                                {grid.rowLabel ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {grid.rowLabel}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <GridViewIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                        {typeof grid.columns === 'number' ? `${grid.columns} columns` : 'responsive'}
                                                        {grid.gap && grid.gap !== 2 && ` · gap ${grid.gap}`}
                                                    </Typography>
                                                )}
                                                {grid.position && (
                                                    <Chip
                                                        label={grid.position}
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                    />
                                                )}
                                                {grid.rowLabel && (() => {
                                                    const rowIsImportant = Boolean(
                                                        grid.important
                                                        || grid.fieldImportantTemplate
                                                        || grid.fields.some((f) => f.important),
                                                    );
                                                    return (
                                                        <Tooltip title={rowIsImportant ? 'Remove important from row' : 'Mark row values as important'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleToggleGridRowImportant(selectedPage, sectionIdx, gridIdx)}
                                                                sx={{ p: 0.5 }}
                                                            >
                                                                {rowIsImportant ? (
                                                                    <StarIcon fontSize="small" color="warning" />
                                                                ) : (
                                                                    <StarBorderIcon fontSize="small" color="action" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                })()}
                                                {(canFullCrud || canEditJson) && (
                                                    <Tooltip title="Edit grid">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditGrid(grid, selectedPage, sectionIdx, gridIdx)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>

                                            {/* Grid Fields - CSS Grid layout with drag-and-drop */}
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={closestCenter}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <SortableContext
                                                    items={grid.fields.map((_, idx) => `field-${selectedPage}-${sectionIdx}-${gridIdx}-${idx}`)}
                                                    strategy={rectSortingStrategy}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: gridColumns,
                                                            gap: grid.gap ?? 2,
                                                        }}
                                                    >
                                                        {grid.fields.map((field: FieldConfig, fieldIdx: number) => {
                                                            const fieldId = `field-${selectedPage}-${sectionIdx}-${gridIdx}-${fieldIdx}`;
                                                            return (
                                                                <Box key={fieldId} sx={{ gridColumn: field.colSpan ? `span ${field.colSpan}` : undefined, minWidth: 0 }}>
                                                                    <ConfigEditorField
                                                                        field={field}
                                                                        fieldId={fieldId}
                                                                        onEdit={() => handleEditField(field, selectedPage, sectionIdx, gridIdx, fieldIdx)}
                                                                        onToggleImportant={() => handleToggleImportant(selectedPage, sectionIdx, gridIdx, fieldIdx)}
                                                                    />
                                                                    {/* Render itemFields for array type fields */}
                                                                    {field.type === 'array' && (field as ArrayFieldConfig).itemFields && (
                                                                        <ItemFieldsList
                                                                            itemFields={(field as ArrayFieldConfig).itemFields}
                                                                            pageIdx={selectedPage}
                                                                            sectionIdx={sectionIdx}
                                                                            gridIdx={gridIdx}
                                                                            fieldIdx={fieldIdx}
                                                                            onToggleImportant={handleToggleImportant}
                                                                            onEditItemField={handleEditItemField}
                                                                            onAddItemField={canFullCrud ? handleAddItemField : undefined}
                                                                            depth={0}
                                                                        />
                                                                    )}
                                                                    {/* Render subFields (for non-array fields with nested structure) */}
                                                                    {field.subFields && field.subFields.length > 0 && (
                                                                        <SubFieldsList
                                                                            parentField={field}
                                                                            subFields={field.subFields}
                                                                            pageIdx={selectedPage}
                                                                            sectionIdx={sectionIdx}
                                                                            gridIdx={gridIdx}
                                                                            fieldIdx={fieldIdx}
                                                                            parentSubFieldPath={[]}
                                                                            onToggleImportant={handleToggleImportant}
                                                                            onEdit={handleEditSubField}
                                                                            onAdd={canFullCrud ? handleAddSubField : undefined}
                                                                            depth={0}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </SortableContext>
                                            </DndContext>
                                            {canFullCrud && (
                                                <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAddField(selectedPage, sectionIdx, gridIdx)}
                                                    sx={{ mt: 1 }}
                                                >
                                                    Add Field
                                                </Button>
                                            )}
                                        </Box>
                                    );
                                })}

                                {/* Add Grid button */}
                                {canFullCrud && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddGrid(selectedPage, sectionIdx)}
                                        sx={{ mt: 1 }}
                                    >
                                        Add Grid
                                    </Button>
                                )}
                            </Box>
                        ))}

                        {/* Add Section button */}
                        {canFullCrud && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddSection(selectedPage)}
                                sx={{ mt: 2 }}
                            >
                                Add Section
                            </Button>
                        )}
                    </Box>
                </>
            )}

            {/* Actions */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Config saved successfully!
                    </Alert>
                )}
                {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                        {uploadError}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        loading={updateLoading}
                        disabled={!hasChanges}
                    >
                        Save Changes
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UndoIcon />}
                        onClick={handleRevert}
                        disabled={!hasChanges}
                    >
                        Revert
                    </Button>
                    {canFullCrud && (
                        <>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".json"
                                style={{ display: 'none' }}
                            />
                            <Tooltip title="Upload JSON config file to replace current config">
                                <Button
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    onClick={handleUploadClick}
                                    color="secondary"
                                >
                                    Upload Config
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ConfigEditor;
